import React, { useState } from 'react'

import clsx from 'clsx'

import Button from 'components/Button'
import CircularProgress from 'components/CircularProgress'
import Grid from 'components/Grid'
import Icon from 'components/Icon'
import Typography from 'components/Typography'
import { thousandSeparator } from 'helpers/formatters'

import IntegrationsWithSnippets from './components/IntegrationsWithSnippets'
import Search from './components/Search'
import SimpleIntegration from './components/SimpleIntegration'

import classes from './Integrations.module.scss'

const INTEGRATIONS_LIMIT = 6
const TOP_INTEGRATIONS_LIMIT = 25

/** @type {import("./types").default} */
const Integrations = ({ listing, className, ...props }) => {
  const { integrations } = listing
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [filteredResults, setFilteredResults] = useState(null)

  const toggleExpanded = () => setIsExpanded(prev => !prev)

  // Ensure we have integrations data
  if (!integrations || integrations.length === 0) {
    return null
  }

  const integrationswithSnippets = []
  const simpleIntegrations = []
  ;(filteredResults || integrations).forEach((integration, counter) => {
    if (integration.avg_integration_rating) {
      integrationswithSnippets.push(integration)
    } else {
      simpleIntegrations.push(integration)
    }
    integration.isVisible = filteredResults || isExpanded || counter < INTEGRATIONS_LIMIT
  })

  const needsExpand = integrations?.length > INTEGRATIONS_LIMIT

  return (
    <Grid container className={clsx('Integrations', classes.root, className)} {...props}>
      <div className={classes.titleContainer}>
        <Typography component="h2" className={classes.title}>
          {`${listing.name} integrations (${thousandSeparator(listing.countIntegrations)})`}
        </Typography>
        {needsExpand && (
          <Search
            className={classes.search}
            setFilteredResults={setFilteredResults}
            setIsLoading={setIsLoading}
            integrations={integrations}
          />
        )}
      </div>
      {!isLoading && filteredResults?.length === 0 && (
        <div className={classes.rootEmpty}>
          <p className={classes.noIntegrationsText}>No results found</p>
        </div>
      )}
      {isLoading ? (
        <Grid item xs={12} className={classes.loadingContainer}>
          <CircularProgress className={classes.circularProgress} color="inherit" size={16} />
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            {integrationswithSnippets.some(({ isVisible }) => isVisible) && (
              <IntegrationsWithSnippets
                integrations={integrationswithSnippets}
                productName={listing.name}
                countNumReviews={listing.countNumReviews}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {simpleIntegrations.some(({ isVisible }) => isVisible) && (
              <>
                <Typography className={classes.subtitle} variant="heading-md">
                  {!!integrationswithSnippets.length ? 'Other top integrations' : 'Top integrations'}
                </Typography>
                <Grid xs={12} spacing={2} container>
                  {simpleIntegrations.map(integration => (
                    <Grid item xs={12} sm={6} key={integration.id}>
                      <SimpleIntegration integration={integration} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Grid>
        </>
      )}
      {integrations.length >= TOP_INTEGRATIONS_LIMIT && isExpanded && (
        <Typography variant="heading-sm" className={classes.helperText}>
          Use the search bar at the top of the section to see how this product integrates with your stack
        </Typography>
      )}
      {filteredResults == null && needsExpand && !isLoading && (
        <Grid item xs={12} className={classes.buttonContainer}>
          <Button onClick={toggleExpanded} className={classes.expandButton} data-testid="expand-button">
            {!isExpanded ? 'Expand list' : 'Collapse list'}
            <Icon icon={['far', `chevron-${isExpanded ? 'up' : 'down'}`]} size={16} />
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default Integrations
